import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiCall } from 'services';

/**
 * VERA-304 — send EITHER. `token` is the emailed link's credential;
 * `certificateId` claims a gift already addressed to the signed-in caller,
 * from their own certificate list, with no email round-trip.
 */
interface ClaimTransferByTokenData {
  token?: string;
  certificateId?: string;
}

/**
 * VRG-138 — completes an ownership transfer from its emailed claim-token
 * link, without requiring a physical NFC re-scan. Called from
 * `ClaimOwnershipSignUp` right after magic-auth verification succeeds, and
 * (VERA-304) from the gift card in the recipient's own certificate list.
 */
const claimTransferByToken = async (data: ClaimTransferByTokenData) => {
  return createApiCall({
    method: 'POST',
    url: `/transfer/customer/claim`,
    data,
    throwError: true,
  });
};

export const useClaimTransferByToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClaimTransferByTokenData) => claimTransferByToken(data),
    /*
     * VERA-304 — `retry: false`, and it is load-bearing rather than tidy.
     *
     * Without it this inherits the global mutation `retry: 1`, and react-query
     * PAUSES a pending retry while `document.visibilityState === 'hidden'`. So a
     * recipient who backgrounds the tab mid-claim leaves `mutateAsync` unsettled
     * forever: neither the `catch` nor the `finally` runs, and the page holds
     * "Claiming your item…" indefinitely with no error and no way out.
     *
     * Reproduced, then confirmed by calling `focusManager.setFocused(true)`, at which
     * point the error rendered immediately — so the request had failed all along and
     * only the retry scheduler was holding the promise open.
     *
     * A claim is not idempotent-safe to retry blindly anyway: the token is consumed
     * on success, so a second attempt fails against an already-used transfer with a
     * message that reads like a broken link. Both routes into the claim share this
     * hook, so the OTP path was affected identically.
     */
    retry: false,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] });
      // VERA-304: the LIST alone is not enough — the certificate DETAIL page reads
      // `['certificate-public', id]`, and without this it kept rendering the
      // pre-claim certificate: the "Gift received" card and "Claim Gift" stayed on
      // screen after a successful claim, and pressing again failed on an
      // already-consumed transfer with a message that read like a broken link.
      // `useAcceptTransfer` and `useCancelTransfer` both already did this; the claim
      // was the odd one out.
      //
      // The in-app route knows its certificate; the EMAIL route carries only a token,
      // so fall back to the key PREFIX (react-query matches by prefix) rather than
      // leaving that route stale.
      queryClient.invalidateQueries({
        queryKey: variables.certificateId
          ? ['certificate-public', variables.certificateId]
          : ['certificate-public'],
      });
    },
  });
};
